import { useState, type FormEvent } from "react";
import { Page } from "../components/AppShell";
import { Badge, Banner, Card, Pager, PanelState } from "../components/ui";
import { useAuth } from "../auth/AuthContext";
import { api } from "../lib/api";
import { date, dateTime, num, relative, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { Admin, AdminRole, InviteRow, PageMeta } from "../lib/types";

const ROLE_NOTE: Record<AdminRole, string> = {
  OWNER: "Everything, including inviting people and deleting customers.",
  ADMIN: "All analytics, plus ending customer sessions.",
  ANALYST: "Read-only. Dashboards and exports, no revocation.",
};

interface IssuedInvite {
  token: string;
  replaced: boolean;
  invite: {
    id: string;
    email: string;
    fullName: string;
    phoneHint: string;
    role: AdminRole;
    expiresAt: string;
  };
}

export function TeamPage() {
  const { admin, isOwner } = useAuth();

  const [adminPage, setAdminPage] = useState(1);
  const [invitePage, setInvitePage] = useState(1);

  const admins = useApi<{ admins: Admin[]; pagination: PageMeta }>(
    isOwner ? "/api/admin/admins" : null,
    { page: adminPage, pageSize: 25 },
  );
  const invites = useApi<{ invites: InviteRow[]; pagination: PageMeta }>(
    isOwner ? "/api/admin/admins/invites" : null,
    { page: invitePage, pageSize: 25 },
  );

  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function act(fn: () => Promise<unknown>, id: string) {
    setBusy(id);
    setMessage(null);
    try {
      await fn();
      admins.reload();
      invites.reload();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "That didn't work.");
    } finally {
      setBusy(null);
    }
  }

  if (!isOwner) {
    return (
      <Page title="Admin team" subtitle="Owner-only">
        <Banner title="You don't have access to this">
          Only an owner can see the admin list and invite people. Your role is{" "}
          {titleCase(admin?.role)}.
        </Banner>
      </Page>
    );
  }

  return (
    <Page
      title="Admin team"
      subtitle="Who can open this console, and what they can do"
    >
      {admin?.isBypass && (
        /* Loud on purpose. It is very easy to demo this console to a room,
           forget which account you are in, and conclude the domain gate is
           broken because you signed in as the bypass. */
        <Banner tone="warning" title="You are signed in on the bypass account">
          This account exists because ADMIN_BYPASS_EMAIL is set on the API. It
          skips the domain gate entirely and has owner rights. Unset
          ADMIN_BYPASS_EMAIL and ADMIN_BYPASS_PASSWORD once the master account
          is set up.
        </Banner>
      )}

      {message && <Banner tone="critical">{message}</Banner>}

      <InviteForm onIssued={() => invites.reload()} />

      <Card
        title="Pending invitations"
        note="Links last seven days and work once. Re-inviting the same address revokes the previous link, so a mistyped address stops working rather than lingering."
      >
        <PanelState
          loading={invites.loading}
          error={invites.error}
          empty={invites.data?.invites.length === 0}
          emptyText="No invitations have been sent yet."
        >
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Invited</th>
                  <th>Mobile</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>By</th>
                  <th>Sent</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invites.data?.invites.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className="cell-strong">{row.fullName}</span>
                      <div className="cell-mute">{row.email}</div>
                    </td>
                    <td className="mono cell-mute">{row.phoneHint}</td>
                    <td className="cell-mute">{titleCase(row.role)}</td>
                    <td>
                      {row.state === "pending" && (
                        <Badge tone="warning">
                          Expires {relative(row.expiresAt)}
                        </Badge>
                      )}
                      {row.state === "accepted" && (
                        <Badge tone="good">
                          Joined {date(row.acceptedAt)}
                        </Badge>
                      )}
                      {row.state === "revoked" && <Badge>Revoked</Badge>}
                      {row.state === "expired" && <Badge>Expired</Badge>}
                    </td>
                    <td className="cell-mute">{row.invitedByEmail}</td>
                    <td className="cell-mute">{dateTime(row.createdAt)}</td>
                    <td style={{ textAlign: "right" }}>
                      {row.state === "pending" && (
                        <button
                          className="btn btn-sm btn-danger"
                          disabled={busy === row.id}
                          onClick={() =>
                            void act(
                              () =>
                                api(`/api/admin/admins/invites/${row.id}`, {
                                  method: "DELETE",
                                }),
                              row.id,
                            )
                          }
                        >
                          {busy === row.id ? "Revoking…" : "Revoke link"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            meta={invites.data?.pagination}
            onChange={setInvitePage}
            busy={invites.stale}
            label="invitations"
          />
        </PanelState>
      </Card>

      <Card
        title="Accounts"
        note="Accounts are disabled, never deleted — an audit row points at the id, and a deleted admin would leave the log naming nobody. Disabling also ends every session that account holds."
      >
        <PanelState loading={admins.loading} error={admins.error}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>Role</th>
                  <th className="num">Live sessions</th>
                  <th>Last sign-in</th>
                  <th>Added</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {admins.data?.admins.map((row) => {
                  const isSelf = row.id === admin?.id;
                  // The master is the account that guarantees someone can
                  // always let people back in. Nothing in this UI may touch it.
                  const locked = row.isMaster;
                  return (
                    <tr key={row.id}>
                      <td>
                        <span className="cell-strong">{row.fullName}</span>
                        <div className="cell-mute">{row.email}</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                          {isSelf && <Badge>You</Badge>}
                          {row.isMaster && <Badge tone="good">Master</Badge>}
                          {row.isBypass && <Badge tone="warning">Bypass</Badge>}
                          {row.disabledAt && (
                            <Badge tone="critical">Disabled</Badge>
                          )}
                        </div>
                      </td>
                      <td>
                        <select
                          className="select"
                          value={row.role}
                          disabled={isSelf || locked || busy === row.id}
                          title={
                            locked
                              ? "The master account is always an owner."
                              : isSelf
                                ? "You can't change your own role — the last owner could otherwise lock everyone out."
                                : ROLE_NOTE[row.role]
                          }
                          onChange={(e) =>
                            void act(
                              () =>
                                api(`/api/admin/admins/${row.id}/role`, {
                                  method: "PATCH",
                                  body: { role: e.target.value },
                                }),
                              row.id,
                            )
                          }
                        >
                          <option value="OWNER">Owner</option>
                          <option value="ADMIN">Admin</option>
                          <option value="ANALYST">Analyst</option>
                        </select>
                      </td>
                      <td className="num">{num(row.liveSessions ?? 0)}</td>
                      <td className="cell-mute">{relative(row.lastLoginAt)}</td>
                      <td className="cell-mute">{date(row.createdAt)}</td>
                      <td style={{ textAlign: "right" }}>
                        {!isSelf && !locked && (
                          <button
                            className={
                              row.disabledAt
                                ? "btn btn-sm"
                                : "btn btn-sm btn-danger"
                            }
                            disabled={busy === row.id}
                            onClick={() =>
                              void act(
                                () =>
                                  api(`/api/admin/admins/${row.id}/disable`, {
                                    method: "POST",
                                    body: { disabled: !row.disabledAt },
                                  }),
                                row.id,
                              )
                            }
                          >
                            {row.disabledAt ? "Re-enable" : "Disable"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager
            meta={admins.data?.pagination}
            onChange={setAdminPage}
            busy={admins.stale}
            label="accounts"
          />
        </PanelState>

        <div style={{ marginTop: 16 }}>
          <h3 className="card-title" style={{ marginBottom: 6 }}>
            What the roles mean
          </h3>
          <ul
            className="card-note"
            style={{ margin: 0, paddingLeft: 18, maxWidth: "72ch" }}
          >
            {(Object.keys(ROLE_NOTE) as AdminRole[]).map((role) => (
              <li key={role} style={{ marginBottom: 3 }}>
                <strong style={{ color: "var(--text-2)" }}>
                  {titleCase(role)}
                </strong>{" "}
                — {ROLE_NOTE[role]}
              </li>
            ))}
          </ul>
          <p className="card-note" style={{ marginTop: 10, maxWidth: "72ch" }}>
            The <strong>master</strong> account is seeded on the server with{" "}
            <span className="mono">npm run admin:create</span>. It is an owner
            that cannot be demoted or disabled — including by itself — so the
            console always has someone who can let people back in.
          </p>
        </div>
      </Card>
    </Page>
  );
}

/**
 * ── Issue an invitation ────────────────────────────────────────────────────
 * The token comes back exactly once and is never stored in readable form, so
 * this form owns the only copy that will ever exist. That is why the link
 * stays on screen until it is dismissed rather than disappearing on the next
 * render — losing it means revoking and re-inviting.
 */
function InviteForm({ onIssued }: { onIssued: () => void }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<AdminRole>("ADMIN");

  const [issued, setIssued] = useState<IssuedInvite | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const link = issued
    ? `${window.location.origin}/invite/${issued.token}`
    : "";

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setCopied(false);

    try {
      const res = await api<IssuedInvite>("/api/admin/admins/invite", {
        method: "POST",
        body: { fullName, email, phone, role },
      });
      setIssued(res);
      setFullName("");
      setEmail("");
      setPhone("");
      setRole("ADMIN");
      onIssued();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create that invitation.");
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard access is refused outside a secure context, and the console
      // may well be served over plain HTTP internally. The link is on screen
      // and selectable, so this is a missing convenience, not a dead end.
      setError("Couldn't copy automatically — select the link and copy it.");
    }
  }

  return (
    <Card
      title="Invite an admin"
      note="Creates the invitation and hands you a one-time link. Send it however you like — the API has no mail server, and the person is in your building. Get the mobile right: every sign-in they ever make texts a code to it, and they will be asked to confirm it before the account is created."
    >
      <form
        onSubmit={submit}
        style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}
      >
        <div className="field" style={{ margin: 0, flex: "1 1 180px" }}>
          <label htmlFor="inviteName">Name</label>
          <input
            id="inviteName"
            className="input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Priya Nair"
            required
          />
        </div>
        <div className="field" style={{ margin: 0, flex: "1 1 220px" }}>
          <label htmlFor="inviteEmail">Work email</label>
          <input
            id="inviteEmail"
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="priya@jkmaxx.com"
            required
          />
        </div>
        <div className="field" style={{ margin: 0, flex: "1 1 170px" }}>
          <label htmlFor="invitePhone">Mobile</label>
          <input
            id="invitePhone"
            className="input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="9876543210"
            required
          />
        </div>
        <div className="field" style={{ margin: 0, flex: "0 1 150px" }}>
          <label htmlFor="inviteRole">Role</label>
          <select
            id="inviteRole"
            className="select"
            value={role}
            onChange={(e) => setRole(e.target.value as AdminRole)}
          >
            <option value="ANALYST">Analyst</option>
            <option value="ADMIN">Admin</option>
            <option value="OWNER">Owner</option>
          </select>
        </div>
        <button className="btn btn-primary" disabled={busy}>
          {busy ? "Creating…" : "Create invitation"}
        </button>
      </form>

      {error && (
        <p className="form-error" style={{ marginTop: 12, marginBottom: 0 }}>
          {error}
        </p>
      )}

      {issued && (
        <div
          className="banner banner-warning"
          style={{ marginTop: 14, display: "block" }}
        >
          <strong>
            Invitation for {issued.invite.email} — send this link
            {issued.replaced && " (the previous link for this address no longer works)"}
          </strong>
          <div className="cell-mute" style={{ marginBottom: 2 }}>
            Sign-in codes will go to {issued.invite.phoneHint}.
          </div>
          <div
            className="mono"
            style={{
              margin: "8px 0",
              padding: "9px 11px",
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              wordBreak: "break-all",
              userSelect: "all",
            }}
          >
            {link}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="btn btn-sm" type="button" onClick={() => void copy()}>
              {copied ? "Copied" : "Copy link"}
            </button>
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => setIssued(null)}
            >
              Dismiss
            </button>
            <span className="cell-mute">
              Works once · expires {relative(issued.invite.expiresAt)} · shown
              only now
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}
